import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { X, AlertTriangle, RefreshCw, ShieldCheck, Loader2, Info, Sparkles, Check, Plus } from 'lucide-react';
import { cn } from '../ui/utils';
import { PortalLayerContext } from '../ui/portalLayer';
import { intakeApi, dictionaryApi, casesApi, apiHelpers } from '../../lib/api';
import type { CaseDocument, ContextAnalysis, CorrelationRisk, ReviewDecision, ReviewStatus, RiskTerm, TopEntity } from '../../types';
import { REVIEW_DECISION_CONFIG } from '../../types';
import { getEntityTypeConfig, useDetectionCategories } from '../../lib/categories';
import { otherSpellings, riskApplied, topEntityState, wordsToRedact } from './topEntityState';
import { finishSummary, plural, riskIsOpen, suggestedDecisions, suggestionCount } from './reviewFinish';
import { isContextTerm, isTermSelected, orderedTerms, riskHasRoles, termKey, termsToRedact, type TermSelection } from './riskTerms';
import { ReviewDecisionSelect } from '../editor/reviewControls';
import { DateContextTag } from '../editor/DateContextTag';
import { groupByRelevance, relevanceOf } from '../editor/relevance';

const entityKey = (e: TopEntity) => e.key ?? `${e.text}::${e.entity_type}`;
const riskId = (r: CorrelationRisk) => r.key ?? `${r.rule_id}::${r.name}`;

type Tab = 'documents' | 'entities' | 'risks' | 'finish';
/** A Finish-tab run: Finalize (with approved redactions), Finalize with no redactions, or Re-finalize. */
type FinishAction = 'finalize' | 'no_redactions' | 'refinalize';

interface ContextRiskPanelProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  /** Called after Redact / Redact all added case dictionary rules, so the page
   *  refreshes the viewer and Results panel like the Case Dict editor does. */
  onEntriesChanged?: () => void;
  /** The case's documents, for the Documents tab (FR-14). */
  documents?: CaseDocument[];
  onSetReviewDecision?: (documentId: string, decision: ReviewDecision | null) => void;
  onBulkSetReviewDecision?: (documentIds: string[], decision: ReviewDecision | null) => Promise<void> | void;
  /** Called after Finalize / Re-finalize finished, so the page reloads the documents. */
  onDocumentsChanged?: () => void;
  /** Called after a risk was dismissed or brought back (the top-bar badge counts open risks). */
  onRisksChanged?: () => void;
}

const SEVERITY_STYLES: Record<CorrelationRisk['severity'], string> = {
  high: 'border-rose-200 bg-rose-50',
  medium: 'border-amber-200 bg-amber-50',
  low: 'border-gray-200 bg-gray-50',
};
const SEVERITY_CHIP: Record<CorrelationRisk['severity'], string> = {
  high: 'bg-rose-200 text-rose-800',
  medium: 'bg-amber-200 text-amber-800',
  low: 'bg-gray-200 text-gray-700',
};

// Plain-language meaning of each correlation-risk type (the info-icon legend).
const RULE_LEGEND: { title: string; body: string }[] = [
  { title: 'Quasi-identifier stack', body: 'Three or more individually-boring attributes (DOB, place, org, vehicle…) co-occur and together single out one person — the "mosaic effect".' },
  { title: 'Medical re-identification', body: 'A date of birth, a person name, and a health facility/condition appear together — re-identifies a patient even after other identifiers are removed.' },
  { title: 'Rare distinctive value', body: 'A value that appears only once in the case, of a distinctive type (MRN, license, passport…) — a near-identifier on its own.' },
  { title: 'Sensitive / health inference', body: 'A protected attribute (religion, ethnicity, politics, health) can be inferred from the data even with no name.' },
  { title: 'Organizational fingerprint', body: 'An org/facility plus an internal reference (case number, badge, EIN) fingerprints a specific unit or record.' },
  { title: 'Relatives / associates', body: 'Multiple people named alongside kinship language leak identity about the subject indirectly.' },
  { title: 'LLM-found (general)', body: 'A non-obvious combination the AI surfaced. "General" ones are reusable patterns worth applying to future cases.' },
];

const ACT = 'flex shrink-0 items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors disabled:cursor-default';
const ACT_PRIMARY = 'bg-primary/10 text-primary hover:bg-primary/20';
const ACT_DARK = 'bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50';
const ACT_DONE = 'bg-green-100 text-green-700';
const ACT_GHOST = 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50';

export function ContextRiskPanel({
  open,
  onClose,
  caseId,
  onEntriesChanged,
  documents = [],
  onSetReviewDecision,
  onBulkSetReviewDecision,
  onDocumentsChanged,
  onRisksChanged,
}: ContextRiskPanelProps) {
  useDetectionCategories(); // entity labels come from the tenant's categories
  const [tab, setTab] = useState<Tab>('documents');
  const [analysis, setAnalysis] = useState<ContextAnalysis | null>(null);
  const [topEntities, setTopEntities] = useState<TopEntity[]>([]);
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus | null>(null);
  const [addedWords, setAddedWords] = useState<Record<string, boolean>>({});
  // Entities whose Redact call is in flight (spinner on the button).
  const [addingWords, setAddingWords] = useState<Record<string, boolean>>({});
  const [redactingAll, setRedactingAll] = useState(false);
  const [addedRiskTerms, setAddedRiskTerms] = useState<Record<string, boolean>>({});
  // Per risk: the terms the reviewer dropped ([X]) or added before Redact all.
  const [termSelections, setTermSelections] = useState<Record<string, TermSelection>>({});
  // Risks applied with the reviewer's own selection (not every preselected term).
  const [appliedRisks, setAppliedRisks] = useState<Record<string, boolean>>({});
  // Risks whose Redact all is in flight, by name (spinner on the button).
  const [redactingRisks, setRedactingRisks] = useState<Record<string, boolean>>({});
  const [promotedRisks, setPromotedRisks] = useState<Record<string, boolean>>({});
  const [showLegend, setShowLegend] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState<string | null>(null);
  const [finalizeMessage, setFinalizeMessage] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState<FinishAction | null>(null);

  useEffect(() => {
    if (!open) {
      setShowLegend(false);
      setFinalizeMessage(null);
    }
  }, [open]);

  // Poll risks, entities and the review status while open. No analysis is kicked
  // here: the worker runs it once the case's detection batch finishes, so kicking
  // on open would either redo a current analysis or analyse a case whose detection
  // is still running.
  const refreshRef = useRef<() => void>(() => {});
  const runningRef = useRef(0);
  // Kept in a ref so the page's (inline) callback doesn't restart the poll.
  const onDocumentsChangedRef = useRef(onDocumentsChanged);
  onDocumentsChangedRef.current = onDocumentsChanged;
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadRisks = () =>
      intakeApi.getCorrelationRisks(caseId).then((a) => !cancelled && setAnalysis(a)).catch(() => {});
    const loadEntities = () =>
      intakeApi.getTopEntities(caseId, 30).then((e) => !cancelled && setTopEntities(e)).catch(() => {});
    const loadStatus = () =>
      casesApi.getReviewStatus(caseId).then((s) => {
        if (cancelled) return;
        setReviewStatus(s);
        // A Finalize / Re-finalize run just finished: let the page reload its documents.
        const running = s.documents.filter((d) => d.batch?.status === 'queued' || d.batch?.status === 'running').length;
        if (runningRef.current > 0 && running < runningRef.current) {
          onDocumentsChangedRef.current?.();
          if (running === 0) setFinalizeMessage(null); // the run finished; Finish shows the result
        }
        runningRef.current = running;
      }).catch(() => {});
    const refresh = () => {
      loadRisks();
      loadEntities();
      loadStatus();
    };
    refreshRef.current = refresh;
    refresh();
    const id = setInterval(refresh, 3000);
    return () => {
      cancelled = true;
      refreshRef.current = () => {};
      clearInterval(id);
    };
  }, [open, caseId]);

  // After a dictionary write: let the page refresh the viewer + Results panel, and
  // re-read this dialog's state from the server now rather than on the next tick.
  const afterDictionaryChange = () => {
    onEntriesChanged?.();
    refreshRef.current();
  };

  const handleReanalyze = useCallback(() => {
    const previous = analysis;
    setReanalyzeError(null);
    setAnalysis((a) => (a ? { ...a, status: 'pending' } : a));
    intakeApi.analyzeContext(caseId).catch((err: unknown) => {
      setAnalysis(previous);
      setReanalyzeError(apiHelpers.handleError(err));
    });
  }, [caseId, analysis]);

  const handleRedactRisk = async (risk: CorrelationRisk) => {
    setRedactingRisks((prev) => ({ ...prev, [risk.name]: true }));
    try {
      await redactRiskTerms(risk);
    } finally {
      setRedactingRisks((prev) => ({ ...prev, [risk.name]: false }));
    }
  };

  // Redact all = only the selected chips (sc-11465): the identifying terms by
  // default, minus any the reviewer dropped, plus any they added.
  const redactRiskTerms = async (risk: CorrelationRisk) => {
    let added = 0;
    let failed = 0;
    for (const t of termsToRedact(risk, termSelections[riskId(risk)], addedRiskTerms)) {
      const key = termKey(t);
      try {
        await dictionaryApi.createEntry({
          scope_type: 'case', scope_id: caseId, word: t.text,
          match_type: 'exact', action: 'always_redact', entity_type: t.entity_type,
        });
        added++;
        setAddedRiskTerms((prev) => ({ ...prev, [key]: true }));
      } catch { failed++; }
    }
    if (failed === 0) setAppliedRisks((prev) => ({ ...prev, [riskId(risk)]: true }));
    if (added > 0) afterDictionaryChange();
  };

  const toggleRiskTerm = (risk: CorrelationRisk, term: RiskTerm, selected: boolean) => {
    const id = riskId(risk);
    setTermSelections((prev) => ({ ...prev, [id]: { ...prev[id], [termKey(term)]: selected } }));
  };

  // Dismiss (or bring back) a risk; stored server-side by its stable key.
  const handleDismissRisk = async (risk: CorrelationRisk) => {
    if (!risk.key) return;
    const dismiss = !risk.dismissed;
    setAnalysis((a) => (a ? { ...a, risks: a.risks.map((r) => (r.key === risk.key ? { ...r, dismissed: dismiss } : r)) } : a));
    try {
      if (dismiss) await intakeApi.dismissRisk(caseId, risk.key);
      else await intakeApi.undoDismissRisk(caseId, risk.key);
      onRisksChanged?.();
    } catch {
      refreshRef.current();
    }
  };

  // Promote a 'general' pattern into a reusable, tenant-global steering hint.
  const handlePromote = async (risk: CorrelationRisk) => {
    const key = risk.name;
    try {
      await intakeApi.promotePattern(caseId, risk);
      setPromotedRisks((prev) => ({ ...prev, [key]: true }));
    } catch { /* ignore */ }
  };

  // Redact a group = every spelling the Results panel groups under it (FB-1007 #5).
  const addEntityRules = async (entity: TopEntity) => {
    for (const word of wordsToRedact(entity)) {
      await dictionaryApi.createEntry({
        scope_type: 'case', scope_id: caseId, word,
        match_type: 'exact', action: 'always_redact', entity_type: entity.entity_type,
      });
    }
    setAddedWords((prev) => ({ ...prev, [entityKey(entity)]: true }));
  };

  const handleAddEntity = async (entity: TopEntity) => {
    const key = entityKey(entity);
    setAddingWords((prev) => ({ ...prev, [key]: true }));
    try {
      await addEntityRules(entity);
      afterDictionaryChange();
    } catch { /* ignore */ } finally {
      setAddingWords((prev) => ({ ...prev, [key]: false }));
    }
  };

  const pendingEntities = topEntities.filter((e) => topEntityState(e) === 'redact' && !addedWords[entityKey(e)]);
  const pendingRuleCount = pendingEntities.reduce((n, e) => n + wordsToRedact(e).length, 0);
  const pendingOccurrences = pendingEntities.reduce((n, e) => n + Math.max(0, e.count - (e.redacted ?? 0)), 0);

  const handleRedactAllEntities = async () => {
    setRedactingAll(true);
    try {
      for (const entity of pendingEntities) {
        try { await addEntityRules(entity); } catch { /* keep going */ }
      }
      afterDictionaryChange();
    } finally {
      setRedactingAll(false);
    }
  };

  // Documents tab: the case's files by relevance group.
  const files = useMemo(() => documents.filter((d) => !d.is_container), [documents]);
  const groups = useMemo(() => groupByRelevance(files), [files]);
  const statusById = useMemo(() => new Map((reviewStatus?.documents ?? []).map((d) => [d.id, d])), [reviewStatus]);

  const acceptSuggested = async (docs: CaseDocument[]) => {
    if (!onBulkSetReviewDecision) return;
    const { responsive, non_responsive } = suggestedDecisions(docs);
    if (responsive.length) await onBulkSetReviewDecision(responsive, 'responsive');
    if (non_responsive.length) await onBulkSetReviewDecision(non_responsive, 'non_responsive');
    refreshRef.current();
  };

  const risks = analysis?.risks ?? [];
  const riskRows = risks.map((risk) => ({
    risk, applied: riskApplied(risk, addedRiskTerms) || !!appliedRisks[riskId(risk)],
  }));
  const summary = finishSummary(reviewStatus, riskRows);
  const openRisks = riskRows.filter((r) => riskIsOpen(r.risk, r.applied));
  // Open risks first (highest severity first), then handled, then dismissed.
  const orderedRisks = [...riskRows].sort((a, b) => {
    const rank = (r: typeof a) => (r.risk.dismissed ? 2 : r.applied ? 1 : 0);
    return rank(a) - rank(b);
  });
  const undecidedCount = files.filter((d) => !d.review_decision).length;

  // "no_redactions" is the same Finalize run on the worker: for a document with
  // nothing approved it finalizes with no redactions (the editor's path too).
  const runBatch = async (action: FinishAction) => {
    const docs =
      action === 'finalize'
        ? summary.finalizeReady
        : action === 'no_redactions'
          ? summary.finalizeNoRedactions
          : summary.outOfDate.filter((d) => !d.blocker);
    if (!docs.length) return;
    const mode = action === 'refinalize' ? 'refinalize' : 'finalize';
    setFinalizing(action);
    setFinalizeMessage(null);
    try {
      const result = await casesApi.finalizeBatch(caseId, docs.map((d) => d.id), mode);
      const verb = mode === 'finalize' ? 'Finalizing' : 'Re-finalizing';
      setFinalizeMessage(
        `${verb} ${plural(result.queued.length, 'document')}${action === 'no_redactions' ? ' with no redactions' : ''}…` +
          (result.needs_review.length ? ` ${plural(result.needs_review.length, 'document')} need${result.needs_review.length === 1 ? 's' : ''} review first.` : '')
      );
      refreshRef.current();
    } catch (err) {
      setFinalizeMessage(`Could not start: ${apiHelpers.handleError(err)}`);
    } finally {
      setFinalizing(null);
    }
  };

  if (!open) return null;

  const tabs: { key: Tab; label: string; badge: number; warn: boolean }[] = [
    { key: 'documents', label: 'Documents', badge: undecidedCount, warn: false },
    { key: 'entities', label: 'Entities', badge: pendingEntities.length, warn: false },
    { key: 'risks', label: 'Risks', badge: openRisks.length, warn: openRisks.some((r) => r.risk.severity === 'high') },
    { key: 'finish', label: 'Finish', badge: suggestionCount(summary), warn: summary.outOfDate.length > 0 },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" role="dialog" aria-modal="true" aria-label="Context awareness">
      <PortalLayerContext.Provider value="z-[110]">
      <div className="bg-white rounded-lg w-[920px] max-h-[88vh] h-[88vh] overflow-hidden flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Context awareness</h2>
            <div className="relative">
              <button
                onClick={() => setShowLegend((v) => !v)}
                className="text-gray-400 hover:text-primary"
                aria-label="What do these mean?"
              >
                <Info className="h-4 w-4" />
              </button>
              {showLegend && (
                <div className="absolute left-0 top-6 z-10 w-[360px] rounded-lg border bg-white p-3 shadow-xl">
                  <p className="mb-2 text-xs font-semibold text-gray-700">Correlation risk types</p>
                  <div className="space-y-2">
                    {RULE_LEGEND.map((l) => (
                      <div key={l.title}>
                        <p className="text-xs font-medium text-gray-800">{l.title}</p>
                        <p className="text-[11px] text-gray-500">{l.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs: switch freely; nothing here blocks */}
        <div role="tablist" aria-label="Review" className="flex gap-1 border-b px-6">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={cn(
                '-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                tab === t.key ? 'border-primary text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'
              )}
            >
              {t.label}
              <span
                data-testid={`tab-badge-${t.key}`}
                className={cn(
                  'min-w-[18px] rounded-full px-1.5 text-center text-[11px] font-semibold leading-[18px] tabular-nums',
                  t.badge === 0 ? 'bg-gray-100 text-gray-400' : t.warn ? 'bg-amber-100 text-amber-800' : 'bg-primary/10 text-primary'
                )}
              >
                {t.badge}
              </span>
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'documents' && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Documents by relevance. Decide what answers the request; the score only suggests a decision.</p>
              {files.length === 0 && <p className="text-xs text-gray-400">No documents in this case yet.</p>}
              {groups.map((g) => {
                const acceptable = suggestedDecisions(g.docs);
                const acceptCount = acceptable.responsive.length + acceptable.non_responsive.length;
                return (
                  <section key={g.key} className="rounded-lg border border-gray-200" data-testid={`relevance-group-${g.key}`}>
                    <div className="flex items-center justify-between border-b bg-gray-50/60 px-3 py-2">
                      <span className="text-sm font-semibold text-gray-800">
                        {g.label} <span className="ml-1 text-xs font-normal text-gray-500">{g.hint ? `${g.hint} · ` : ''}{g.docs.length}</span>
                      </span>
                      {acceptCount > 0 && onBulkSetReviewDecision && (
                        <button type="button" className={cn(ACT, ACT_PRIMARY)} onClick={() => acceptSuggested(g.docs)}>
                          Accept suggested ({acceptCount})
                        </button>
                      )}
                    </div>
                    <div className="divide-y">
                      {g.docs.map((doc) => {
                        const st = statusById.get(doc.id);
                        const score = relevanceOf(doc);
                        const finalized = doc.status === 'completed';
                        const pending = st?.pending_suggestions ?? 0;
                        return (
                          <div key={doc.id} className="flex items-center gap-3 px-3 py-2" data-testid="review-document">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm text-gray-800" title={doc.name}>{doc.name}</p>
                              <p className="text-[11px] text-gray-500">
                                {score !== null ? `Relevance ${score}%` : 'Not scored'}
                                {!finalized && pending > 0 && ` · ${plural(pending, 'pending suggestion')}`}
                                {' · '}
                                <span className={cn(doc.out_of_date ? 'font-medium text-amber-700' : finalized ? 'text-green-700' : 'text-gray-500')}>
                                  {doc.out_of_date ? 'Finalized, out of date' : finalized ? 'Finalized' : 'Not finalized'}
                                </span>
                              </p>
                            </div>
                            {onSetReviewDecision && <ReviewDecisionSelect doc={doc} onChange={onSetReviewDecision} />}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          {tab === 'entities' && (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4">
                <p className="text-xs text-gray-500">
                  Top terms across the case, grouped like the Results panel. Redact adds a case rule, so it also covers documents added later.
                </p>
                <button
                  type="button"
                  onClick={handleRedactAllEntities}
                  disabled={pendingEntities.length === 0 || redactingAll}
                  aria-busy={redactingAll}
                  className={cn(ACT, pendingEntities.length ? ACT_DARK : ACT_DONE)}
                >
                  {redactingAll && <Loader2 className="h-3 w-3 animate-spin" />}
                  {pendingEntities.length ? `Redact all (${pendingEntities.length})` : 'All handled'}
                </button>
              </div>
              {pendingEntities.length > 0 && (
                <p className="text-xs text-gray-600" data-testid="redact-all-preview">
                  Adds {plural(pendingRuleCount, 'case rule')} and redacts {plural(pendingOccurrences, 'occurrence')} still visible.
                  Finalized documents that contain them become out of date.
                </p>
              )}
              {topEntities.length === 0 && (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> waiting for detection…
                </p>
              )}
              <div className="space-y-1.5">
                {topEntities.map((e) => {
                  const key = entityKey(e);
                  const state = topEntityState(e);
                  const added = addedWords[key];
                  const adding = addingWords[key];
                  const spellings = otherSpellings(e);
                  const done = state !== 'redact' || added;
                  return (
                    <div key={key} data-testid="top-entity"
                      className={cn('flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5', done && 'bg-gray-50')}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm text-gray-800" title={e.text}>{e.text}</p>
                        {spellings && <p className="truncate text-[11px] text-gray-500" title={spellings}>{spellings}</p>}
                        <p className="text-[10px] uppercase text-gray-400">
                          {getEntityTypeConfig(e.entity_type).label}<DateContextTag tag={e.date_context} /> · {e.count}×{(e.redacted ?? 0) > 0 && ` · ${e.redacted}/${e.count} redacted`}
                        </p>
                      </div>
                      {state === 'redact' ? (
                        <button
                          onClick={() => handleAddEntity(e)}
                          disabled={added || adding || redactingAll}
                          aria-busy={adding}
                          className={cn(ACT, added ? ACT_DONE : ACT_PRIMARY)}
                        >
                          {adding && <Loader2 className="h-3 w-3 animate-spin" />}
                          {added ? 'Added' : 'Redact'}
                        </button>
                      ) : (
                        <span className="flex shrink-0 items-center gap-1 rounded bg-green-100 px-2 py-1 text-xs font-medium text-green-700">
                          <Check className="h-3 w-3" /> {state === 'redacted' ? 'Redacted' : 'In dictionary'}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {tab === 'risks' && (
            <div className="space-y-3 flex flex-col min-h-full">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <AlertTriangle className="h-4 w-4 text-amber-500" /> Correlation risks
                </div>
                <button onClick={handleReanalyze} className="flex items-center gap-1 text-xs text-gray-500 hover:text-primary">
                  <RefreshCw className={cn('h-3 w-3', analysis?.status === 'pending' && 'animate-spin')} /> Re-analyze
                </button>
              </div>
              <p className="text-xs text-gray-500">
                Combinations of individually-boring values that can re-identify a person or org even after
                names are redacted. Redact all removes the selected terms, the ones that identify someone;
                context terms only explain the risk. Use × to drop a term, or click a term to add it.
              </p>
              {reanalyzeError && <p className="text-xs text-rose-600">Could not start analysis: {reanalyzeError}</p>}
              {(!analysis || analysis.status === 'pending') && (analysis?.risks.length ? (
                // Re-analyzing: the previous risks stay listed below.
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" /> analyzing case context…
                </p>
              ) : (
                <div role="status" className="flex flex-1 min-h-[240px] flex-col items-center justify-center gap-3 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <p className="text-sm font-medium text-gray-800">Analyzing case context…</p>
                  <p className="max-w-xs text-xs text-gray-500">
                    Looking for combinations of values that could re-identify someone. Risks appear here when it's done.
                  </p>
                </div>
              ))}
              {analysis?.status === 'none' && (
                <p className="text-xs text-gray-400">
                  Not analyzed yet. Analysis runs automatically when detection finishes; use Re-analyze to run it now.
                </p>
              )}
              {analysis?.status === 'failed' && (
                <p className="text-xs text-rose-600">Analysis failed{analysis.error ? `: ${analysis.error}` : ''}. Try Re-analyze.</p>
              )}
              {analysis?.status === 'ready' && analysis.risks.length === 0 && (
                <p className="text-xs text-gray-400">No non-trivial correlation risks found.</p>
              )}
              {orderedRisks.map(({ risk, applied }, idx) => (
                <RiskCard
                  key={risk.key ?? `${risk.rule_id}-${idx}`}
                  risk={risk}
                  added={applied}
                  selection={termSelections[riskId(risk)]}
                  pending={addedRiskTerms}
                  onToggleTerm={(term, selected) => toggleRiskTerm(risk, term, selected)}
                  onRedact={() => handleRedactRisk(risk)}
                  redacting={!!redactingRisks[risk.name]}
                  promoted={!!promotedRisks[risk.name]}
                  onPromote={() => handlePromote(risk)}
                  onDismiss={risk.key ? () => handleDismissRisk(risk) : undefined}
                />
              ))}
            </div>
          )}

          {tab === 'finish' && (
            <FinishTab
              summary={summary}
              loaded={reviewStatus !== null}
              finalizing={finalizing}
              message={finalizeMessage}
              onGo={setTab}
              onFinalize={() => runBatch('finalize')}
              onFinalizeNoRedactions={() => runBatch('no_redactions')}
              onRefinalize={() => runBatch('refinalize')}
            />
          )}
        </div>
      </div>
      </PortalLayerContext.Provider>
    </div>
  );
}

function FinishTab({
  summary: s,
  loaded,
  finalizing,
  message,
  onGo,
  onFinalize,
  onFinalizeNoRedactions,
  onRefinalize,
}: {
  summary: ReturnType<typeof finishSummary>;
  loaded: boolean;
  finalizing: FinishAction | null;
  message: string | null;
  onGo: (tab: Tab) => void;
  onFinalize: () => void;
  onFinalizeNoRedactions: () => void;
  onRefinalize: () => void;
}) {
  if (!loaded) {
    return (
      <p className="text-xs text-gray-400 flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> loading case status…
      </p>
    );
  }
  const refinalizable = s.outOfDate.filter((d) => !d.blocker);
  const status: { label: string; value: string; go: Tab }[] = [
    { label: 'Documents decided', value: `${s.decided} of ${s.total}${s.undecided ? ` · ${s.undecided} undecided` : ''}`, go: 'documents' },
    { label: REVIEW_DECISION_CONFIG.responsive.label, value: `${s.responsiveFinalized} finalized · ${s.responsiveNotFinalized} not finalized`, go: 'documents' },
    { label: 'Pending suggestions', value: `${plural(s.pendingOnResponsive, 'suggestion')} on responsive docs`, go: 'documents' },
    { label: 'Open risks', value: `${s.openRisks} of ${s.totalRisks}`, go: 'risks' },
  ];
  const nothingToDo = s.responsiveNotFinalized === 0 && s.outOfDate.length === 0 && s.running.length === 0;
  return (
    <div className="space-y-5">
      <p className="text-xs text-gray-500">Where the case stands right now. Nothing here blocks you; these are suggestions.</p>
      <div className="grid grid-cols-2 gap-3">
        {status.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2.5" data-testid="finish-status">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{row.label}</p>
              <p className="text-sm text-gray-800">{row.value}</p>
            </div>
            <button type="button" className={cn(ACT, ACT_GHOST)} onClick={() => onGo(row.go)}>Open</button>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Suggested</p>
        {nothingToDo && <p className="text-sm text-gray-600">Every responsive document is finalized and up to date.</p>}

        {s.responsiveNotFinalized > 0 && (
          <div className="rounded-lg border border-gray-200 p-3 space-y-2" data-testid="suggest-finalize">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{plural(s.responsiveNotFinalized, 'responsive document')} not finalized yet</p>
                {s.finalizeReady.length > 0 && (
                  <p className="text-xs text-gray-500 truncate">{s.finalizeReady.map((d) => d.name).join(', ')}</p>
                )}
              </div>
              {(s.finalizeReady.length > 0 || s.finalizeNoRedactions.length === 0) && (
                <button
                  type="button"
                  className={cn(ACT, ACT_DARK)}
                  disabled={s.finalizeReady.length === 0 || finalizing !== null}
                  onClick={onFinalize}
                >
                  {finalizing === 'finalize' && <Loader2 className="h-3 w-3 animate-spin" />}
                  Finalize {s.finalizeReady.length}
                </button>
              )}
            </div>
            {s.finalizeNoRedactions.length > 0 && (
              <div className="flex items-start justify-between gap-3" data-testid="suggest-finalize-no-redactions">
                <div className="min-w-0">
                  <p className="text-xs text-gray-700">
                    Nothing approved on {plural(s.finalizeNoRedactions.length, 'document')}: finalize with no redactions
                  </p>
                  <p className="text-xs text-gray-500 truncate">{s.finalizeNoRedactions.map((d) => d.name).join(', ')}</p>
                </div>
                <button
                  type="button"
                  className={cn(ACT, ACT_DARK)}
                  disabled={finalizing !== null}
                  onClick={onFinalizeNoRedactions}
                >
                  {finalizing === 'no_redactions' && <Loader2 className="h-3 w-3 animate-spin" />}
                  Finalize {s.finalizeNoRedactions.length} with no redactions
                </button>
              </div>
            )}
            {s.finalizeNeedsReview.length > 0 && (
              <div className="space-y-0.5">
                <p className="text-[11px] font-medium text-gray-600">Need review first:</p>
                {s.finalizeNeedsReview.map((d) => (
                  <p key={d.id} className="text-xs text-gray-600"><span className="font-medium">{d.name}</span> — {d.blocker}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {s.outOfDate.length > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 space-y-2" data-testid="suggest-refinalize">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-gray-900">
                {plural(s.outOfDate.length, 'finalized document')} out of date
              </p>
              <button
                type="button"
                className={cn(ACT, ACT_DARK)}
                disabled={refinalizable.length === 0 || finalizing !== null}
                onClick={onRefinalize}
              >
                {finalizing === 'refinalize' && <Loader2 className="h-3 w-3 animate-spin" />}
                Re-finalize {refinalizable.length}
              </button>
            </div>
            {s.outOfDate.map((d) => (
              <p key={d.id} className="text-xs text-gray-700">
                <span className="font-medium">{d.name}</span> — {d.stale_reasons.join('; ')}
                {d.blocker && <span className="text-amber-800"> ({d.blocker})</span>}
              </p>
            ))}
          </div>
        )}

        {s.running.length > 0 && (
          <p className="text-xs text-gray-600 flex items-center gap-1" data-testid="finalize-running">
            <Loader2 className="h-3 w-3 animate-spin" />
            {s.running.some((d) => d.batch?.mode === 'refinalize') ? 'Re-finalizing' : 'Finalizing'} {plural(s.running.length, 'document')}…
          </p>
        )}
        {s.failed.map((d) => (
          <p key={d.id} className="text-xs text-rose-700">
            <span className="font-medium">{d.name}</span> — {d.batch?.mode === 'refinalize' ? 'Re-finalize' : 'Finalize'} failed: {d.batch?.error}
          </p>
        ))}
        {message && <p className="text-xs text-gray-600">{message}</p>}
      </div>
    </div>
  );
}

export function RiskCard({
  risk,
  added,
  selection,
  pending = {},
  onToggleTerm,
  onRedact,
  redacting = false,
  promoted = false,
  onPromote,
  onDismiss,
}: {
  risk: CorrelationRisk;
  added: boolean;
  selection?: TermSelection;
  pending?: Record<string, boolean>;
  onToggleTerm?: (term: RiskTerm, selected: boolean) => void;
  onRedact: () => void;
  redacting?: boolean;
  promoted?: boolean;
  onPromote?: () => void;
  onDismiss?: () => void;
}) {
  const dismissed = !!risk.dismissed;
  const hasRoles = riskHasRoles(risk);
  const terms = orderedTerms(risk);
  const toRedact = termsToRedact(risk, selection, pending).length;
  // Chips can be dropped / added back until the card is applied (or dismissed).
  const editable = !added && !dismissed && !!onToggleTerm;
  const firstContext = terms.findIndex((t) => isContextTerm(t, hasRoles));
  return (
    <div className={cn('rounded-lg border p-3', dismissed ? 'border-gray-200 bg-white opacity-60' : SEVERITY_STYLES[risk.severity])} data-testid="risk-card">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn('rounded-sm px-1.5 py-0.5 text-[10px] font-semibold uppercase', SEVERITY_CHIP[risk.severity])}>
              {risk.severity}
            </span>
            <span className="text-sm font-medium text-gray-800">{risk.name}</span>
            {risk.scope === 'general' && (
              <span className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">general</span>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-600" title={risk.rationale}>{risk.description}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {risk.scope === 'general' && onPromote && (
            <button
              onClick={onPromote}
              disabled={promoted}
              title="Save this pattern as a reusable steering hint for future cases"
              className={cn('flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors',
                promoted ? 'bg-indigo-100 text-indigo-700' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100')}
            >
              <Sparkles className="h-3 w-3" /> {promoted ? 'Promoted' : 'Promote'}
            </button>
          )}
          {onDismiss && !added && (
            <button
              onClick={onDismiss}
              title={dismissed ? 'Show this risk as open again' : 'Not a concern for this request'}
              className={cn(ACT, dismissed ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : ACT_GHOST)}
            >
              {dismissed ? 'Dismissed' : 'Dismiss'}
            </button>
          )}
          {!dismissed && (
            <button
              onClick={onRedact}
              disabled={added || redacting || toRedact === 0}
              aria-busy={redacting}
              title={added ? undefined : 'Add the selected terms to the case dictionary'}
              className={cn(ACT, added ? ACT_DONE : ACT_PRIMARY, !added && toRedact === 0 && 'opacity-50')}
            >
              {redacting && <Loader2 className="h-3 w-3 animate-spin" />}
              {added ? 'Added' : `Redact all (${toRedact})`}
            </button>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {terms.map((t, i) => (
          <RiskTermChip
            key={termKey(t)}
            term={t}
            first={i === firstContext && i > 0}
            context={isContextTerm(t, hasRoles)}
            selected={isTermSelected(t, hasRoles, selection)}
            redacted={!!t.in_dictionary || !!pending[termKey(t)]}
            editable={editable}
            onToggle={(selected) => onToggleTerm?.(t, selected)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * One term on a risk card (sc-11465). Selected terms are what Redact all adds;
 * they carry an [X] to drop them. Unselected ones (context terms, Allow List
 * terms, dropped terms) are dashed and are added back with a click.
 */
function RiskTermChip({
  term,
  first,
  context,
  selected,
  redacted,
  editable,
  onToggle,
}: {
  term: RiskTerm;
  /** The first context term: a line break and a "Context" caption go before it. */
  first: boolean;
  context: boolean;
  selected: boolean;
  redacted: boolean;
  editable: boolean;
  onToggle: (selected: boolean) => void;
}) {
  const label = (
    <>
      <span className="truncate">{term.text}</span>
      <span className="text-gray-400">
        ·{getEntityTypeConfig(term.entity_type).label}
        <DateContextTag tag={term.date_context} />
      </span>
      {term.allow_listed ? (
        <span className="rounded-sm bg-gray-100 px-1 text-[10px] text-gray-500">allow list</span>
      ) : context ? (
        <span className="rounded-sm bg-gray-100 px-1 text-[10px] text-gray-500">context</span>
      ) : null}
    </>
  );
  const caption = first ? (
    <span className="basis-full pt-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">
      Context: explains the risk, not redacted unless you add it
    </span>
  ) : null;
  const base = 'inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 text-[11px]';
  const common = { 'data-testid': 'risk-term', 'data-selected': String(selected), 'data-role': term.role ?? '' };

  if (redacted) {
    return (
      <>
        {caption}
        <span {...common} className={cn(base, 'border-green-200 bg-green-50 text-green-800')} title="The case dictionary redacts this term">
          <Check className="h-3 w-3 shrink-0" />
          {label}
        </span>
      </>
    );
  }
  if (selected) {
    return (
      <>
        {caption}
        <span {...common} className={cn(base, 'border-gray-300 bg-white font-medium text-gray-800 shadow-sm')}>
          {label}
          {editable && (
            <button
              type="button"
              onClick={() => onToggle(false)}
              aria-label={`Remove ${term.text}`}
              title="Don't redact this term"
              className="-mr-0.5 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </span>
      </>
    );
  }
  if (!editable) {
    return (
      <>
        {caption}
        <span {...common} className={cn(base, 'border-dashed border-gray-300 bg-white/40 text-gray-500')}>
          {label}
        </span>
      </>
    );
  }
  return (
    <>
      {caption}
      <button
        {...common}
        type="button"
        onClick={() => onToggle(true)}
        aria-label={`Add ${term.text}`}
        title="Redact this term too"
        className={cn(base, 'border-dashed border-gray-300 bg-white/40 text-gray-500 hover:border-gray-400 hover:bg-white hover:text-gray-700')}
      >
        <Plus className="h-3 w-3 shrink-0" />
        {label}
      </button>
    </>
  );
}
